import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import UserAvatar from './UserAvatar';
import {
  Sparkles, TrendingUp, AlertTriangle, Users, Target, Zap,
  ChevronDown, ChevronRight, Loader2, RefreshCw, Crown,
  Award, Building2, Calendar, ArrowUp, ArrowDown, Minus,
  Lightbulb, CheckCircle2, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GM Insights Panel - Executive-level AI insights across all teams
 * Shows strategic overview, at-risk teams/people, patterns, and recommendations
 */
const GMInsightsPanel = ({ className = "" }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [expandedPeople, setExpandedPeople] = useState({});

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/eos/leadership/gm-insights`);
      setInsights(response.data);
    } catch (err) {
      console.error('Error fetching GM insights:', err);
      if (err.response?.status === 403) {
        setError('GM insights require leadership access');
      } else {
        setError('Unable to load GM insights');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInsights();
    setRefreshing(false);
    toast.success('GM insights updated');
  };

  const getHealthColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 8) return 'bg-green-100 dark:bg-green-900/30';
    if (score >= 6) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  const getRiskColor = (level) => {
    if (level === 'high') return 'text-red-600 bg-red-100 dark:bg-red-900/30';
    if (level === 'medium') return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-green-600 bg-green-100 dark:bg-green-900/30';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving' || trend === 'up') return <ArrowUp className="h-4 w-4 text-green-600" />;
    if (trend === 'declining' || trend === 'down') return <ArrowDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
            <p className="text-sm text-muted-foreground mt-3">Loading GM Insights...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="text-center">
            <AlertTriangle className="h-10 w-10 mx-auto text-yellow-600 mb-3" />
            <p className="text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = insights || {};
  const overallHealth = data.overall_health || {};
  const isMock = data.is_mock;

  return (
    <Card className={`border-purple-200 dark:border-purple-900 ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-purple-600" />
              GM / Executive Insights
              {isMock && <Badge variant="outline" className="text-xs">Demo Mode</Badge>}
            </CardTitle>
            <CardDescription>AI-powered strategic overview across all teams</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Overall Health */}
        <div className={`p-4 rounded-lg ${getHealthBg(overallHealth.score || 7)}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              <span className="font-semibold">Overall Organization Health</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-3xl font-bold ${getHealthColor(overallHealth.score || 7)}`}>
                {overallHealth.score || 7}/10
              </span>
              {getTrendIcon(overallHealth.trend)}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">{overallHealth.summary}</p>
        </div>

        {/* Meeting Quality Trends */}
        {data.meeting_quality_trends && (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted rounded-lg text-center">
              <Calendar className="h-5 w-5 mx-auto text-blue-600 mb-1" />
              <div className="text-2xl font-bold">{data.meeting_quality_trends.avg_score || 0}</div>
              <div className="text-xs text-muted-foreground">Avg Meeting Score</div>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <Clock className="h-5 w-5 mx-auto text-orange-600 mb-1" />
              <div className="text-2xl font-bold">{data.meeting_quality_trends.overtime_percentage || 0}%</div>
              <div className="text-xs text-muted-foreground">Running Overtime</div>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <CheckCircle2 className="h-5 w-5 mx-auto text-green-600 mb-1" />
              <div className="text-2xl font-bold">{data.meeting_quality_trends.completion_rate || 0}%</div>
              <div className="text-xs text-muted-foreground">L10 Completion</div>
            </div>
          </div>
        )}

        <Tabs defaultValue="risks" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="risks" className="flex-1">
              <AlertTriangle className="h-4 w-4 mr-1" />
              At Risk
            </TabsTrigger>
            <TabsTrigger value="themes" className="flex-1">
              <Zap className="h-4 w-4 mr-1" />
              Patterns
            </TabsTrigger>
            <TabsTrigger value="actions" className="flex-1">
              <Lightbulb className="h-4 w-4 mr-1" />
              Actions
            </TabsTrigger>
          </TabsList>

          {/* At Risk Tab */}
          <TabsContent value="risks" className="space-y-4">
            {/* Teams at Risk */}
            {data.teams_at_risk?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Teams at Risk ({data.teams_at_risk.length})
                </h4>
                <div className="space-y-2">
                  {data.teams_at_risk.map((team, idx) => (
                    <Collapsible
                      key={idx}
                      open={expandedTeams[idx]}
                      onOpenChange={() => setExpandedTeams(prev => ({ ...prev, [idx]: !prev[idx] }))}
                    >
                      <CollapsibleTrigger className="w-full">
                        <div className={`p-3 rounded-lg ${getRiskColor(team.risk_level)} flex items-center justify-between`}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            <span className="font-medium">{team.team_name}</span>
                            <Badge variant={team.risk_level === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                              {team.risk_level}
                            </Badge>
                          </div>
                          {expandedTeams[idx] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-3 pt-2 space-y-2">
                          {team.reasons?.map((reason, ridx) => (
                            <div key={ridx} className="text-sm flex items-start gap-2">
                              <span className="text-red-500">•</span>
                              <span>{reason}</span>
                            </div>
                          ))}
                          {team.recommendations?.map((rec, ridx) => (
                            <div key={ridx} className="text-sm flex items-start gap-2 text-green-700 dark:text-green-400">
                              <span>→</span>
                              <span>{rec}</span>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </div>
            )}

            {/* People at Risk */}
            {data.people_at_risk?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  People at Risk ({data.people_at_risk.length})
                </h4>
                <div className="space-y-2">
                  {data.people_at_risk.map((person, idx) => (
                    <Collapsible
                      key={idx}
                      open={expandedPeople[idx]}
                      onOpenChange={() => setExpandedPeople(prev => ({ ...prev, [idx]: !prev[idx] }))}
                    >
                      <CollapsibleTrigger className="w-full">
                        <div className={`p-3 rounded-lg ${getRiskColor(person.risk_level)} flex items-center justify-between`}>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            <span className="font-medium">{person.name}</span>
                            {person.team && <span className="text-xs">({person.team})</span>}
                            <Badge variant={person.risk_level === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                              {person.risk_level}
                            </Badge>
                          </div>
                          {expandedPeople[idx] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-3 pt-2 space-y-2">
                          {person.reasons?.map((reason, ridx) => (
                            <div key={ridx} className="text-sm flex items-start gap-2">
                              <span className="text-red-500">•</span>
                              <span>{reason}</span>
                            </div>
                          ))}
                          {person.recommendations?.map((rec, ridx) => (
                            <div key={ridx} className="text-sm flex items-start gap-2 text-green-700 dark:text-green-400">
                              <span>→</span>
                              <span>{rec}</span>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </div>
            )}

            {(!data.teams_at_risk || data.teams_at_risk.length === 0) && 
             (!data.people_at_risk || data.people_at_risk.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-10 w-10 mx-auto text-green-600 mb-2" />
                <p>No significant risks identified</p>
              </div>
            )}
          </TabsContent>

          {/* Patterns Tab */}
          <TabsContent value="themes" className="space-y-4">
            {data.recurring_ids_themes?.length > 0 ? (
              <div className="space-y-3">
                {data.recurring_ids_themes.map((theme, idx) => (
                  <div key={idx} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{theme.theme}</span>
                      <Badge variant="outline" className="text-xs">
                        {theme.frequency}x across teams
                      </Badge>
                    </div>
                    {theme.teams_affected?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {theme.teams_affected.map((team, tidx) => (
                          <Badge key={tidx} variant="secondary" className="text-xs">
                            {team}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {theme.recommendation && (
                      <p className="text-sm text-green-700 dark:text-green-400">
                        → {theme.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Zap className="h-10 w-10 mx-auto mb-2" />
                <p>No recurring patterns identified</p>
              </div>
            )}
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions" className="space-y-4">
            {/* Strategic Recommendations */}
            {data.strategic_recommendations?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-yellow-500" />
                  Strategic Recommendations
                </h4>
                <div className="space-y-2">
                  {data.strategic_recommendations.map((rec, idx) => (
                    <div key={idx} className="p-3 bg-muted rounded-lg text-sm flex items-start gap-2">
                      <span className="h-5 w-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Urgent Attention */}
            {data.urgent_attention?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  Urgent Attention
                </h4>
                <div className="space-y-2">
                  {data.urgent_attention.map((item, idx) => (
                    <div key={idx} className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800 text-sm">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Wins to Celebrate */}
            {data.wins_to_celebrate?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-600">
                  <Award className="h-4 w-4" />
                  Wins to Celebrate
                </h4>
                <div className="space-y-2">
                  {data.wins_to_celebrate.map((win, idx) => (
                    <div key={idx} className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg text-sm flex items-start gap-2">
                      <span className="text-green-600">🎉</span>
                      <span>{win}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!data.strategic_recommendations || data.strategic_recommendations.length === 0) &&
             (!data.urgent_attention || data.urgent_attention.length === 0) &&
             (!data.wins_to_celebrate || data.wins_to_celebrate.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <Lightbulb className="h-10 w-10 mx-auto mb-2" />
                <p>No specific actions at this time</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {isMock && (
          <div className="text-xs text-center text-muted-foreground pt-2 border-t">
            Demo mode - configure API key for real AI analysis
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GMInsightsPanel;
